import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Form, Formik } from 'formik'
import { hooks as coreHooks } from 'mage-core'
import { EducationAnalytics, handleEducationError, useEducationClient } from 'education-tools'
import { hooks as i18nHooks, selectors as i18nSelectors } from 'mage-i18n'
import {
  ParagraphSecondary,
  SpacerVertical,
  TitlePrimary,
  AlertBannerError,
  LinkLegal
} from 'mage-deps/@klarna/bubble-ui'
import validationSchema from './validationSchema'
import Fields from './Fields'
import { useHelpContext } from '../hooks'

const TOO_MANY_REQUESTS_STATUS_CODE = 429

CreateTicketPage.propTypes = {
  setTicketId: PropTypes.func.isRequired
}

export default function CreateTicketPage({ setTicketId }) {
  const t = i18nHooks.useTranslator()
  const language = useSelector(i18nSelectors.language)
  const educationClient = useEducationClient()
  const tokenHelper = coreHooks.useTokenHelper()
  const formRef = useRef(null)
  const [showTempUnavailableError, setShowTempUnavailableError] = useState(false)
  const [showGenericError, setShowGenericError] = useState(false)
  const { navigation } = useHelpContext()

  const mids = tokenHelper.getMids()

  const initialValues = {
    mid: mids.length === 1 ? mids[0] : null,
    topic: null,
    subject: '',
    description: ''
  }

  const contactPageLink = (
    <LinkLegal
      data-testid="create-ticket-page-contact-page-link"
      onClick={() => navigation.push('contact', {})}
    >
      {t('hqm.support-page.create-ticket.contact-page-link')}
    </LinkLegal>
  )

  return (
    <div data-testid="create-ticket-page">
      <EducationAnalytics category="uhqm/page/create-support-ticket" action="pageview" />

      {showTempUnavailableError && (
        <div data-testid="temp-unavailable-error">
          <AlertBannerError
            content={t('hqm.support-page.create-ticket.service-unavailable-error', {
              link: contactPageLink
            })}
          />
          <SpacerVertical small />
        </div>
      )}
      {showGenericError && (
        <div data-testid="generic-error">
          <AlertBannerError
            content={t('hqm.support-page.create-ticket.generic-error', {
              link: contactPageLink
            })}
          />
          <SpacerVertical small />
        </div>
      )}
      <TitlePrimary>{t('hqm.support-page.create-ticket.title')}</TitlePrimary>
      <ParagraphSecondary>{t('hqm.support-page.create-ticket.instructions')}</ParagraphSecondary>
      <SpacerVertical small />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit}>
        <Form ref={formRef}>
          <Fields />
        </Form>
      </Formik>
    </div>
  )

  async function onSubmit(values, actions) {
    try {
      const form = formRef.current
      const data = new FormData(form)

      setShowTempUnavailableError(false)
      setShowGenericError(false)

      data.append('description', values.description)
      data.append('language', language)
      const response = await educationClient.post('/v1/tickets', data)

      setTicketId(response.data.id)
    } catch (error) {
      actions.setSubmitting(false)

      if (error.response?.status === TOO_MANY_REQUESTS_STATUS_CODE) {
        setShowTempUnavailableError(true)
      } else {
        setShowGenericError(true)
      }
      handleEducationError(error)
    }
  }
}
